'use client';
import type { TokenSizeProps } from '@mnd-frontend/icons/utils/type';
import {
  MEDIA_SMALL,
  RADIUS_XL,
  SPACING_2,
  SPACING_3,
  WEB_PURPLE_REG,
  WEB_RED_REG,
} from '@mnd-frontend/ui/shared/tokens';
import type { ComponentType, MouseEventHandler } from 'react';
import styled from 'styled-components';
import Button from '../Button';
import IconBadge from '../IconBadge';

const Card = styled.li`
  display: flex;
  flex-direction: column;
  gap: ${SPACING_3};
  justify-content: space-between;
  background: #ffffff;
  border-radius: ${RADIUS_XL};
  padding: ${SPACING_3};

  @media (max-width: ${MEDIA_SMALL}px) { 
    gap: ${SPACING_2};
    padding: ${SPACING_2};
  }
`;

const Header = styled.header`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${SPACING_2};
`;

const Title = styled.h2` // @todo: web/h3/desktop
  color: ${WEB_PURPLE_REG};
  font-size: 1.75rem;
  font-weight: 500;
  line-height: 120%;
  letter-spacing: -0.0525rem;
  margin: 0;
  white-space: pre-line; //allow line breaks in title

  @media (max-width: ${MEDIA_SMALL}px) { // @todo: web/h3/mobile
    font-size: 1.5rem;
    letter-spacing: -0.045rem;
  }
`;

const Description = styled.p`// @todo: web/body/reg
  color: ${WEB_PURPLE_REG};
  font-size: 1rem;
  font-weight: 400;
  line-height: 150%;
  
  @media (max-width: ${MEDIA_SMALL}px) { // @todo: web/body/sm
    font-size: 0.875rem;
  }
`;

const EmailLine = styled.a`
  color: ${WEB_RED_REG};
  font-weight: 600;
  line-height: 2rem;
  width: fit-content;
`;

type FeatureCardProps<T> = {
  id: string;
  title: string;
  titleAs?: 'h2' | 'h3';
  description: string;
  icon?: ComponentType<TokenSizeProps>;
  action?: {
    text: string;
    onClick?: T extends () => void ? T : MouseEventHandler<HTMLAnchorElement | HTMLButtonElement>;
    href?: string;
    newTab?: boolean;
  };
  email?: {
    label: string;
    onClick?: MouseEventHandler<HTMLAnchorElement>;
  };
  as?: 'div' | 'li';
};

const FeatureCard = <T,>({
  id,
  title,
  titleAs,
  description,
  icon,
  action,
  email,
  as = 'li',
}: FeatureCardProps<T>) => {
  return (
    <Card as={as}>
      <Header>
        <Title as={titleAs} id={id}>
          {title}
        </Title>
        {icon && <IconBadge icon={icon} color={email?.label ? WEB_PURPLE_REG : WEB_RED_REG} />}
      </Header>
      <Description>{description}</Description>
      {email?.label && !action && (
        <EmailLine
          href={`mailto:${email.label}`}
          {...(email.onClick ? { onClick: email.onClick } : {})}
        >
          {email.label}
        </EmailLine>
      )}
      {action && (
        <Button
          configuration="ghost"
          aria-labelledby={id}
          {...(action?.href
            ? {
                href: action.href,
                newTab: action?.newTab,
                onClick: action?.onClick,
              }
            : { onClick: action.onClick, type: 'button' })}
        >
          {action.text}
        </Button>
      )}
    </Card>
  );
};

export default FeatureCard;
